import { zodResolver as baseZodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import type { z } from 'zod';

interface DebugOptions {
  withDebugLog?: boolean;
  name?: string;
}

/**
 * A Zod resolver for React Hook Form that provides additional debugging options.
 *
 * @param schema - The Zod schema to use for validation.
 * @param options - Optional debugging options, including whether to log input and output, and a name for the resolver.
 * @returns A Resolver function that can be used with React Hook Form.
 */
export function zodResolver<T extends z.ZodType>(
  schema: T,
  options: DebugOptions = {
    withDebugLog: false,
  },
): Resolver<z.infer<T>> {
  const resolver = baseZodResolver(schema);

  return async (values, context, resolverOptions) => {
    const { withDebugLog, name = 'Form' } = options;

    if (withDebugLog) {
      // TODO: Use logger
      console.group(`${name} - Debug Resolver Input`);
      console.log('Values:', values);
      console.log('Context:', context);
      console.log('Options:', resolverOptions);
      console.groupEnd();
    }

    const result = await resolver(values, context, resolverOptions);

    if (withDebugLog) {
      // TODO: Use logger
      console.group(`${name} - Debug Resolver Result`);
      console.log('Valid:', Object.keys(result.errors).length === 0);
      if (Object.keys(result.errors).length === 0) {
        console.log('Errors:', result.errors);
      }
      console.log('Values:', result.values);
      console.groupEnd();
    }

    return result;
  };
}
